import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { DateTime } from 'luxon';
import { BehaviorSubject, Observable, Subscriber } from 'rxjs';

import { LoginEventType } from '../login-event-type';
import { AuthApiService } from './auth-api.service';
import { BearerToken } from '../models/bearer-token';
import { getDecodedJwt } from '../../helpers/jwt-helpers';
import { ObservableCacheService } from './observable-cache.service';
import { ClientService } from './client.service';

export enum AmrTypes {
  PWD = 'PWD',
  MFA = 'MFA',
  MFA_REQUIRED = 'MFA_REQUIRED',
  REFRESH = 'REFRESH',
}

export enum ClaimTypes {
  authenticationMethod = 'http://schemas.microsoft.com/ws/2008/06/identity/claims/authenticationmethod'
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  static readonly AccessTokenStorageName = 'accessToken';
  static readonly AccessTokenExpiresStorageName = 'accessToken_expires';
  static readonly RefreshTokenStorageName = 'refreshToken';

  static readonly QuestionnairePortalAuthCookieName = '.portalAuth';
  static readonly QuestionnairePortalAuthCookieExpiryName = 'SharedAuthExpiry'

  /**
   * The authentication method used to obtain the access token
   * This is used to determine if the user has logged in with a password or MFA.
   * Should be one of {@link AmrTypes}
   */
  static readonly AccessTokenAmrStorageName = 'accessToken_amr';

  private loginEventSubject = new BehaviorSubject<LoginEventType | undefined>(undefined);

  constructor(private authApiService: AuthApiService, private router: Router, private observableCacheService: ObservableCacheService, private clientService: ClientService) {
    this.loginEventSubject.subscribe(event => this.handleLogEvents(event));
  }

  private handleLogEvents(event: LoginEventType | undefined): void {
    if (event === LoginEventType.Login) {
      // Placeholder!
    }
  }

  public get access_token(): string | null {
    return localStorage.getItem(AuthService.AccessTokenStorageName);
  }
  public get expires(): string | null {
    return localStorage.getItem(AuthService.AccessTokenExpiresStorageName);
  }

  public get amr(): string | null {
    return localStorage.getItem(AuthService.AccessTokenAmrStorageName);
  }

  public get refreshToken(): string | null {
    return localStorage.getItem(AuthService.RefreshTokenStorageName);
  }

  /**
   * Checks that the user's bearer token is valid,
   * and that it the amr claim is {@link AmrTypes.PWD} or {@link AmrTypes.MFA}
   * @returns true if the user is logged in, false otherwise
   */
  isLoggedIn(): boolean {

    if (!this.accessTokenIsValid()) {
      return false;
    }

    const amr = this.amr ?? '';
    if ([AmrTypes.PWD, AmrTypes.MFA, AmrTypes.REFRESH].includes(amr as AmrTypes)) {
      return true;
    }
    return false;
  }

  /**
   * Checks that the user's bearer token is valid,
   * and that it the amr claim is {@link AmrTypes.MFA_REQUIRED}
   * @returns true if the user is logged in, false otherwise
   */
  isPartiallyLoggedIn() {
    if (!this.accessTokenIsValid()) {
      return false;
    }

    const amr = this.amr ?? '';
    if (amr == AmrTypes.MFA_REQUIRED) {
      return true;
    }

    return false;
  }

  /**
   * Checks that the user's bearer token is valid and not expired
   * if the token is invalid, the user will be logged out
   */
  private accessTokenIsValid() {
    var access_token = this.access_token
    const tokenExpiration = this.expires;

    if (!access_token || !tokenExpiration) {
      return false;
    }

    const expiresDateTime = DateTime.fromISO(tokenExpiration);
    if (expiresDateTime < DateTime.now()) {
      this.logOut();
      return false;
    }
    return true;
  }

  /**
   *
   * @param username
   * @param password
   * @returns an {@link BearerToken} observable
   */
  public login(username: string, password: string): Observable<BearerToken> {
    return new Observable<BearerToken>(observer => {
      this.authApiService.getBearerToken(username, password).subscribe(this.processAuthenticationResponse(observer, LoginEventType.Login));
    });
  }

  /**
   * Attempts to authenticate the user with a two-factor/MFA code
   * Requires a previous PWD authenticated request to have been made
   * @param mfaCode
   * @returns an {@link BearerToken} observable
   */
  public loginWithMfaCode(mfaCode: string): Observable<BearerToken> {
    return new Observable<BearerToken>(observer => {
      this.authApiService.getBearerTokenWithMfaCode(mfaCode).subscribe(this.processAuthenticationResponse(observer, LoginEventType.Login));
    });
  }

  /**
 * Attempts to enable MFA for the user
 * Requires a previous PWD authenticated request to have been made
 */
  public enableMfa(mfaCode: string) {
    return this.authApiService.enableMfa(mfaCode);
  }

  public GetMfaConfig(setup: boolean = false) {
    return this.authApiService.GetMfaConfig(setup);
  }

  public removeMfa(mfaCode: string) {
    return this.authApiService.removeMfa(mfaCode);
  }

  public forgotPassword(username: string) {
    return this.authApiService.forgotPassword(username);
  }

  public verifyResetToken(resetToken: string) {
    return this.authApiService.verifyResetToken(resetToken);
  }

  public resetPassword(resetToken: string, newPassword: string, ignoreWarnings: boolean) {
    return this.authApiService.resetPassword(resetToken, newPassword, ignoreWarnings);
  }

  public changePassword(currentPassword: string, newPassword: string, ignoreWarnings: boolean) {
    return this.authApiService.changePassword(currentPassword, newPassword, ignoreWarnings);
  }

  private processAuthenticationResponse(observer: Subscriber<BearerToken>, event: LoginEventType) {
    return {
      next: (bearerToken: BearerToken) => this.AuthSuccess(bearerToken, observer, event),
      error: (error: any) => observer.error(error)
    };
  }

  private AuthSuccess(bearerToken: BearerToken, observer: Subscriber<BearerToken>, event: LoginEventType) {
    this.storeBearerToken(bearerToken)
    observer.next(bearerToken);
    this.loginEventSubject.next(event);
  }

  private storeBearerToken(bearerToken: BearerToken) {
    localStorage.setItem(AuthService.AccessTokenStorageName, bearerToken.access_token);
    localStorage.setItem(AuthService.AccessTokenExpiresStorageName, DateTime.now().plus({ seconds: bearerToken.expires_in }).toISO());
    localStorage.setItem(AuthService.RefreshTokenStorageName, bearerToken.refresh_token);
    const decodedJwt = getDecodedJwt(bearerToken.access_token);
    localStorage.setItem(AuthService.AccessTokenAmrStorageName, decodedJwt[ClaimTypes.authenticationMethod]);
  }

  private removeBearerToken() {
    localStorage.removeItem(AuthService.AccessTokenStorageName);
    localStorage.removeItem(AuthService.AccessTokenExpiresStorageName);
    localStorage.removeItem(AuthService.AccessTokenAmrStorageName);
    localStorage.removeItem(AuthService.RefreshTokenStorageName);
  }
  
  /**
   * Removes the authentication cookies related to the Questionnaire Portal by setting their values to empty
   * and their expiration date to a past date, effectively deleting them.
   */
  private removePortalAuthCookie() {
    const expiryDate = new Date(0).toUTCString(); // equivalent to Thu, 01 Jan 1970 00:00:00 UTC
    document.cookie = `${AuthService.QuestionnairePortalAuthCookieName}=; expires=${expiryDate}; path=/;`;
    document.cookie = `${AuthService.QuestionnairePortalAuthCookieExpiryName}=; expires=${expiryDate}; path=/;`;
  }

  private removeMfaReminderSupressed() {
    this.clientService.unsupressMfaReminder();
  }

  public logOut() {
    this.removeBearerToken();
    this.removePortalAuthCookie();
    this.removeMfaReminderSupressed();
    this.loginEventSubject.next(LoginEventType.Logout);
    this.router.navigate(['login']);
  }

  public RefreshToken() {

    // Cache requests when refreshing, so simultaneous requests don't all try to refresh the token
    return this.observableCacheService.getCachedObservable('refreshToken', () => {
      return new Observable<BearerToken>(observer => {
        this.authApiService.RefreshToken(this.refreshToken).subscribe({
          next: (bearerToken: BearerToken) => {
            this.processAuthenticationResponse(observer, LoginEventType.Refresh).next(bearerToken);
            observer.complete();
          },
          error: (error: any) => {
            observer.error(error);
            observer.complete(); // todo - I dont think we are calling complete on many/any of our observables
          }
      });
      });
    });
  }
}
